import { X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AppReportDialog } from "#/components/AppReportDialog";

const DISMISSED_KEY = "beta-banner-dismissed-v1";

export function BetaBanner() {
	const { t } = useTranslation("common");
	const [dismissed, setDismissed] = useState(
		() =>
			typeof window !== "undefined" &&
			sessionStorage.getItem(DISMISSED_KEY) === "1",
	);
	const [reportOpen, setReportOpen] = useState(false);

	if (dismissed) return null;

	function dismiss() {
		sessionStorage.setItem(DISMISSED_KEY, "1");
		setDismissed(true);
	}

	return (
		<>
			<div className="relative overflow-hidden border-b border-border bg-muted">
				{/* Dot grid texture */}
				<div
					className="pointer-events-none absolute inset-0 opacity-[0.35]"
					style={{
						backgroundImage:
							"radial-gradient(circle, var(--border) 1px, transparent 1px)",
						backgroundSize: "18px 18px",
					}}
				/>
				{/* Left edge primary accent */}
				<div className="absolute inset-y-0 left-0 w-[3px] bg-primary" />

				<div className="page-wrap relative z-10 flex flex-col gap-1 py-2 pl-5 sm:min-h-[40px] sm:flex-row sm:items-center sm:gap-3">
					{/* Row 1 (mobile) / left section (desktop): BETA chip */}
					<div className="flex items-center gap-2">
						<span className="relative block h-[7px] w-[7px] shrink-0 rounded-full bg-primary">
							<span className="absolute inset-0 animate-ping rounded-full bg-primary opacity-60" />
						</span>
						<span className="rounded border border-primary/30 bg-primary/10 px-1.5 py-0.5 text-[10px] font-black uppercase tracking-[0.12em] text-primary-accessible">
							BETA
						</span>
						{/* Dismiss button — mobile only (top-right of first row) */}
						<button
							type="button"
							onClick={dismiss}
							aria-label={t("betaBanner.dismiss")}
							className="ml-auto shrink-0 rounded p-0.5 text-muted-foreground/40 transition-colors hover:bg-border hover:text-foreground sm:hidden"
						>
							<X className="h-3 w-3" />
						</button>
					</div>

					{/* Divider — desktop only */}
					<span className="hidden h-3.5 w-px shrink-0 self-center bg-border sm:block" />

					{/* Row 2 (mobile) / middle section (desktop): message + report CTA */}
					<div className="flex min-w-0 flex-1 items-center gap-2">
						<p className="min-w-0 flex-1 text-[11.5px] leading-snug text-muted-foreground">
							{t("betaBanner.message")}
						</p>
						<button
							type="button"
							onClick={() => setReportOpen(true)}
							className="flex shrink-0 items-center gap-1 self-center text-[10px] font-semibold text-primary-accessible transition-opacity hover:opacity-70"
						>
							{t("betaBanner.reportCta")}
							<span aria-hidden className="text-[10px]">
								→
							</span>
						</button>
					</div>

					{/* Dismiss button — desktop only */}
					<button
						type="button"
						onClick={dismiss}
						aria-label={t("betaBanner.dismiss")}
						className="ml-0.5 hidden shrink-0 self-center rounded p-0.5 text-muted-foreground/40 transition-colors hover:bg-border hover:text-foreground sm:block"
					>
						<X className="h-3 w-3" />
					</button>
				</div>
			</div>

			<AppReportDialog open={reportOpen} onOpenChange={setReportOpen} />
		</>
	);
}
